import React, { useEffect, useState } from "react";
import { useAuthContext } from "@/lib/auth-context";

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

type Role = "teacher" | "coach" | "sponsor" | "guardian";

type AiTool = {
  id: string;
  kind: string | null;
  owner_role: string | null;
  tc_cost: number | null;
  meta?: {
    label?: string;
    description?: string;
    surface_hint?: string;
  } | null;
};

type UserToolSetting = {
  tool_id: string;
  enabled: boolean;
  settings: any;
  tool?: AiTool | null;
};

interface Props {
  role: Role;
  manageHref: string;
}

export const CurrentToolsCard: React.FC<Props> = ({ role, manageHref }) => {
  const { session } = useAuthContext();
  const accessToken = session?.access_token;
  const [tools, setTools] = useState<UserToolSetting[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const headers: Record<string, string> = {};
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

        const res = await fetch(`${CORE_API}/api/v1/marketplace/me-tools?role=${role}`, { headers });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error?.message || json?.error || "Unable to load AI tools");

        if (!cancelled) {
          const payload = json.data ?? json;
          const enabledTools = Array.isArray(payload.tools)
            ? payload.tools.filter((t: UserToolSetting) => t.enabled)
            : [];
          setTools(enabledTools);
        }
      } catch (err) {
        console.error("CurrentToolsCard load error", err);
        if (!cancelled) setTools([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [role, accessToken]);

  return (
    <div className="flex flex-col gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-slate-900">Your AI tools</div>
          <div className="text-xs text-slate-500">
            These tools are currently active in your {role} portal.
          </div>
        </div>
        <a
          href={manageHref}
          className="text-xs font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900"
        >
          Manage
        </a>
      </div>

      {loading && <div className="text-xs text-slate-500">Loading tools…</div>}

      {!loading && tools.length === 0 && (
        <div className="text-xs text-slate-500">
          No tools active yet. Click <span className="font-semibold">Manage</span> to pick your stack.
        </div>
      )}

      {!loading && tools.length > 0 && (
        <ul className="space-y-1 text-xs">
          {tools.map((t) => {
            const label = t.tool?.meta?.label || t.tool?.meta?.description || t.tool?.id || t.tool_id;
            const hint = t.tool?.meta?.surface_hint;
            const cost = t.tool?.tc_cost ?? null;

            return (
              <li key={t.tool_id} className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="font-medium text-slate-900">{label}</span>
                  {hint ? <span className="text-[11px] text-slate-500">{hint}</span> : null}
                </div>
                <div className="text-right text-[11px] text-slate-500">
                  {cost && cost > 0 ? `${cost} credits / use` : "Included"}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

